import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ProductCard from "@/components/ProductCard";
import { api } from "@/lib/api";

export default function Catalog() {
    const [products, setProducts] = useState([]);
    const [collections, setCollections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [params, setParams] = useSearchParams();
    const collection = params.get("collection") || "All";
    const cat = params.get("cat") || "All";
    const purity = params.get("purity") || "All";
    const sort = params.get("sort") || "newest";

    useEffect(() => {
        setLoading(true);
        Promise.all([api.get("/products"), api.get("/collections")])
            .then(([p, c]) => { setProducts(p.data); setCollections(c.data); })
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        let r = products;
        if (collection !== "All") r = r.filter((p) => (p.collection_handles || []).includes(collection));
        if (cat !== "All") r = r.filter((p) => p.category === cat);
        if (purity !== "All") r = r.filter((p) => p.purity === purity);
        if (sort === "weight_asc") r = [...r].sort((a, b) => a.weight_grams - b.weight_grams);
        if (sort === "weight_desc") r = [...r].sort((a, b) => b.weight_grams - a.weight_grams);
        return r;
    }, [products, collection, cat, purity, sort]);

    const updateParam = (k, v) => {
        const next = new URLSearchParams(params);
        if (v === "All" || !v) next.delete(k); else next.set(k, v);
        // when changing collection, drop category filter (and vice versa) to avoid conflicting empty states
        if (k === "collection" && v && v !== "All") next.delete("cat");
        if (k === "cat" && v && v !== "All") next.delete("collection");
        setParams(next, { replace: true });
    };

    const purities = useMemo(() => ["All", ...Array.from(new Set(products.map((p) => p.purity).filter(Boolean)))], [products]);
    const selectedCollection = collections.find((c) => c.handle === collection);
    const heading = collection !== "All" ? (selectedCollection?.title || collection) : (cat !== "All" ? cat : "The Catalog");
    const subheading = collection !== "All" ? "Collection" : (cat !== "All" ? "Category" : "All Pieces");

    return (
        <div className="bg-paper min-h-screen">
            {/* HEADER */}
            <section className="border-b border-soft">
                <div className="max-w-[1480px] mx-auto px-6 lg:px-12 pt-16 pb-12 text-center">
                    <p className="overline">{subheading}</p>
                    <h1 className="font-serif text-5xl sm:text-6xl text-ink mt-3">{heading}</h1>
                    <p className="mt-5 text-charcoal max-w-xl mx-auto">Browse our manufacturing range. Tap any piece to view details, then add it to your enquiry to share with us on WhatsApp.</p>
                </div>
            </section>

            {/* COLLECTION PILLS */}
            {collections.length > 0 && (
                <div className="border-b border-soft overflow-x-auto">
                    <div className="max-w-[1480px] mx-auto px-6 lg:px-12 py-5 flex items-center gap-2 whitespace-nowrap">
                        <button
                            onClick={() => updateParam("collection", "All")}
                            data-testid="col-pill-all"
                            className={`px-4 py-2 text-[10px] uppercase tracking-[0.22em] border transition-colors ${collection === "All" && cat === "All" ? 'bg-ink text-paper border-ink' : 'bg-paper text-ink border-soft hover:border-bronze'}`}
                        >
                            All Collections
                        </button>
                        {collections.map((c) => (
                            <button
                                key={c.handle}
                                onClick={() => updateParam("collection", c.handle)}
                                data-testid={`col-pill-${c.handle}`}
                                className={`px-4 py-2 text-[10px] uppercase tracking-[0.22em] border transition-colors ${collection === c.handle ? 'bg-ink text-paper border-ink' : 'bg-paper text-ink border-soft hover:border-bronze'}`}
                            >
                                {c.title} <span className="ml-1 text-bronze-deep/70">{c.products_count}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* FILTER BAR */}
            <div className="max-w-[1480px] mx-auto px-6 lg:px-12 py-6 flex flex-wrap items-center gap-4 border-b border-soft">
                <Select value={purity} onValueChange={(v) => updateParam("purity", v)}>
                    <SelectTrigger data-testid="filter-purity" className="w-[160px] bg-white border-soft text-ink h-10 text-xs uppercase tracking-[0.18em]">
                        <SelectValue placeholder="Purity" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-soft text-ink">
                        {purities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                </Select>

                <Select value={sort} onValueChange={(v) => updateParam("sort", v)}>
                    <SelectTrigger data-testid="filter-sort" className="w-[200px] bg-white border-soft text-ink h-10 text-xs uppercase tracking-[0.18em]">
                        <SelectValue placeholder="Sort" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-soft text-ink">
                        <SelectItem value="newest">Newest</SelectItem>
                        <SelectItem value="weight_asc">Light → Heavy</SelectItem>
                        <SelectItem value="weight_desc">Heavy → Light</SelectItem>
                    </SelectContent>
                </Select>

                {(collection !== "All" || cat !== "All" || purity !== "All") && (
                    <Link to="/catalog" className="text-[10px] uppercase tracking-[0.22em] text-bronze-deep underline-grow" data-testid="catalog-clear">Clear filters</Link>
                )}

                <span data-testid="catalog-count" className="ml-auto text-[10px] uppercase tracking-[0.22em] text-muted-ink">{filtered.length} pieces</span>
            </div>

            {/* GRID */}
            <div className="max-w-[1480px] mx-auto px-6 lg:px-12 py-12">
                {loading ? (
                    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div key={`skeleton-${i}`}>
                                <div className="aspect-[4/5] bg-ivory animate-pulse" />
                                <div className="h-4 mt-3 w-3/4 mx-auto bg-ivory animate-pulse" />
                            </div>
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <p data-testid="catalog-empty" className="text-muted-ink py-20 text-center font-serif text-2xl">No pieces match your filters.</p>
                ) : (
                    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
                        {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
                    </div>
                )}
            </div>
        </div>
    );
}
